import { useTranslation } from "react-i18next";

import { formatClock, formatFileSize } from "@/lib/format";
import type { Message, MessageMedia, MessageStatus } from "./queries";
import { OUTBOUND, isOptimistic, mediaUrl } from "./queries";

type Tick = "clock" | "single" | "double" | "failed";

/**
 * Clock queued, `✓` sent, `✓✓` delivered, `✓✓` blue read — the convention
 * every WhatsApp user already knows. Reusing it means nothing has to be
 * explained; inventing our own would mean everything does.
 */
const TICKS: Partial<Record<MessageStatus, { tick: Tick; tone: string }>> = {
  queued: { tick: "clock", tone: "text-wa-meta" },
  sent: { tick: "single", tone: "text-wa-meta" },
  delivered: { tick: "double", tone: "text-wa-meta" },
  read: { tick: "double", tone: "text-wa-tick" },
  failed: { tick: "failed", tone: "text-wa-danger" },
};

function TickIcon({ tick }: { tick: Tick }) {
  const stroke = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  } as const;
  switch (tick) {
    case "clock":
      return (
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <circle cx="8" cy="8" r="6" {...stroke} />
          <path d="M8 4.5V8l2.5 1.5" {...stroke} />
        </svg>
      );
    case "single":
      return (
        <svg viewBox="0 0 16 11" width="16" height="11" aria-hidden="true">
          <path d="M3 5.8 6.3 9 13 1.8" {...stroke} />
        </svg>
      );
    case "double":
      return (
        <svg viewBox="0 0 16 11" width="16" height="11" aria-hidden="true">
          <path d="M1 5.8 4.3 9 11 1.8M7.6 8.2l.8.8L15 1.8" {...stroke} />
        </svg>
      );
    case "failed":
      return (
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <circle cx="8" cy="8" r="7" fill="currentColor" />
          <path d="M8 4.5v4.2M8 11.2v.3" {...stroke} stroke="#fff" strokeWidth={1.8} />
        </svg>
      );
  }
}

function Attachment({ media, outbound }: { media: MessageMedia; outbound: boolean }) {
  const { t } = useTranslation();

  if (!media.is_downloaded) {
    // Ours is still on its way up, theirs on its way down.
    return (
      <p className="text-xs text-wa-meta">
        {outbound && media.filename ? media.filename : t("inbox.mediaPending")}
      </p>
    );
  }

  const url = mediaUrl(media.id);

  // Clients send voice notes constantly; an audio message that has to be
  // downloaded and opened in another app is an audio message nobody listens to.
  if (media.mime_type.startsWith("audio/")) {
    return <audio controls preload="none" src={url} className="mt-1 w-56 max-w-full" />;
  }
  if (media.mime_type.startsWith("image/")) {
    return (
      <a href={url} target="_blank" rel="noreferrer">
        <img
          src={url}
          alt={media.filename || t("inbox.image")}
          loading="lazy"
          className="mt-1 max-h-64 max-w-full rounded-[6px]"
        />
      </a>
    );
  }
  if (media.mime_type.startsWith("video/")) {
    return (
      <video
        controls
        preload="none"
        src={url}
        className="mt-1 max-h-64 max-w-full rounded-[6px]"
      />
    );
  }
  // A document is a card, as in WhatsApp: what it is and how heavy, before
  // anyone opens it.
  const name = media.filename || t("inbox.attachment");
  const extension = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1) : "";
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="mt-1 flex items-center gap-3 rounded-[6px] bg-black/5 px-3 py-2.5"
    >
      <svg
        viewBox="0 0 24 24"
        width="28"
        height="28"
        aria-hidden="true"
        className="shrink-0 text-wa-icon"
      >
        <path
          fill="currentColor"
          d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm-1 7V3.5L18.5 9z"
        />
      </svg>
      <span className="min-w-0">
        <span className="block truncate text-wa-text">{name}</span>
        <span className="text-xs text-wa-meta">
          {[
            extension.toUpperCase(),
            media.size_bytes ? formatFileSize(media.size_bytes) : "",
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </span>
    </a>
  );
}

export function MessageBubble({
  message,
  tail = false,
  onRetry,
}: {
  message: Message;
  /** The last of a run from the same side, which is the one WhatsApp tails. */
  tail?: boolean;
  onRetry?: (message: Message) => void;
}) {
  const { t } = useTranslation();
  const outbound = message.direction === OUTBOUND;
  const failed = message.status === "failed";
  const tick = outbound ? TICKS[message.status] : undefined;
  // The time rides on the text's last line only when the text is the last
  // thing in the bubble; after a photo or an error it takes a line of its own.
  const timeOnTextLine = Boolean(message.body) && message.media.length === 0 && !failed;

  return (
    <div className={`flex ${outbound ? "justify-end" : "justify-start"}`}>
      <div
        className={`wa-bubble ${outbound ? "wa-bubble-out" : "wa-bubble-in"} ${
          tail ? "wa-tail" : ""
        } ${isOptimistic(message) ? "opacity-60" : ""}`}
      >
        {message.body ? (
          <p className="break-words whitespace-pre-wrap">
            {message.body}
            {timeOnTextLine ? (
              <span className="wa-meta-spacer" aria-hidden="true" />
            ) : null}
          </p>
        ) : null}

        {message.kind === "template" && !message.body ? (
          <p className="text-wa-meta italic">{t("inbox.templateSent")}</p>
        ) : null}

        {message.media.map((media) => (
          <Attachment key={media.id} media={media} outbound={outbound} />
        ))}

        {failed ? (
          <div className="mt-2 text-xs text-wa-danger">
            {/* The reason Meta gave, verbatim. "Falló el envío" alone leaves
                the agent with nothing to do about it. */}
            <p>{message.error_message || t("inbox.sendFailed")}</p>
            {/* Not for a file: retrying resends the text, and the file itself
                is no longer in the browser. The agent attaches it again. */}
            {onRetry && message.media.length === 0 ? (
              <button
                type="button"
                onClick={() => onRetry(message)}
                className="btn btn-secondary mt-1 px-2 py-0.5"
              >
                {t("inbox.retry")}
              </button>
            ) : null}
          </div>
        ) : null}

        {timeOnTextLine ? null : <span className="block h-3.5" aria-hidden="true" />}
        <span className="wa-meta">
          <time dateTime={message.timestamp}>{formatClock(message.timestamp)}</time>
          {tick ? (
            <span
              role="img"
              aria-label={t(`inbox.status.${message.status}`)}
              title={t(`inbox.status.${message.status}`)}
              className={tick.tone}
            >
              <TickIcon tick={tick.tick} />
            </span>
          ) : null}
        </span>
      </div>
    </div>
  );
}
